/**
 * Pannello staff Blagoweb: attivazione massiva della rete di invio e stato
 * deliverability di tutti i clienti. Visibile solo agli utenti is_admin.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  ShieldCheck, Rocket, RefreshCw, Loader2, Copy, AlertTriangle, Check, X, Server,
} from 'lucide-react';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { adminApi, type DnsRecordToSet } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import { cn } from '@/lib/utils';
import { InviteCodes } from '@/components/admin/InviteCodes';

export function AdminPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin-overview'], queryFn: () => adminApi.overview(), retry: false });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<{ account: string; domain: string; ok: boolean; message: string; records?: DnsRecordToSet[] }[]>([]);

  const bulkMut = useMutation({
    mutationFn: (ids: string[]) => adminApi.bulkActivate(ids),
    onSuccess: (r) => {
      setResults(r.results);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['admin-overview'] });
      toast.success(`${r.results.filter((x) => x.ok).length} domini registrati`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Attivazione fallita'),
  });

  const recheckMut = useMutation({
    mutationFn: () => adminApi.recheck(),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-overview'] });
      toast.success(`${r.checked} domini controllati, ${r.authenticated} autenticati`);
    },
  });

  // L'interruttore vero: «Pronto» vuol dire solo che Brevo ha accettato il
  // DNS, e finché non si accende qui la posta continua a uscire dall'SMTP
  // della casella. L'API c'era già, mancava il tasto.
  const reteMut = useMutation({
    mutationFn: ({ domain, enabled }: { domain: string; enabled: boolean }) => adminApi.toggle(domain, enabled),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['admin-overview'] });
      toast.success(r.domain.enabled ? `${r.domain.domain}: in rete` : `${r.domain.domain}: tornato all'SMTP della casella`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Non riuscito'),
  });

  if (q.isError) {
    return (
      <div className="h-screen flex bg-background">
        <InboxSidebar />
        <div className="flex-1 flex items-center justify-center text-muted-foreground">
          Area riservata allo staff Blagoweb.
        </div>
      </div>
    );
  }

  const clients = q.data?.clients ?? [];
  const totals = q.data?.totals;
  const net = q.data?.network;
  const bounceRate = totals && totals.sent > 0 ? Math.round((totals.bounced / totals.sent) * 100) : 0;

  function toggle(id: string) {
    setSelected((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-5xl mx-auto px-8 py-8 space-y-6">

          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold">Pannello Blagoweb</h1>
              <p className="text-sm text-muted-foreground">Rete di invio e salute della consegna, per tutti i clienti.</p>
            </div>
          </div>

          {/* Stato infrastruttura */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Server className="h-4 w-4" /> Infrastruttura</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-3">
                {[
                  { ok: net?.apiConfigured, l: 'API Brevo' },
                  { ok: net?.smtpConfigured, l: 'Relay SMTP' },
                  { ok: net?.webhookConfigured, l: 'Webhook eventi' },
                ].map((x) => (
                  <div key={x.l} className="flex items-center gap-2 rounded-md border px-3 py-2">
                    {x.ok ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-destructive" />}
                    <span>{x.l}</span>
                  </div>
                ))}
              </div>
              {net?.webhookUrl && (
                <div className="rounded-md bg-muted/40 p-3 text-xs font-mono flex items-start gap-2">
                  <span className="flex-1 break-all">{net.webhookUrl}</span>
                  <button onClick={() => { void navigator.clipboard.writeText(net.webhookUrl!); toast.success('URL copiato'); }}>
                    <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Incolla questo URL in Brevo → Impostazioni → Webhook (eventi: delivered, opened, click, hard_bounce, soft_bounce, spam).
              </p>
            </CardContent>
          </Card>

          {/* Numeri globali */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { v: totals?.sent ?? 0, l: 'Email inviate (30gg)' },
              { v: `${bounceRate}%`, l: 'Tasso di rimbalzo', warn: bounceRate > 3 },
              { v: totals?.domains ?? 0, l: 'Domini registrati' },
              { v: totals?.active ?? 0, l: 'Domini attivi' },
            ].map((c) => (
              <div key={c.l} className={cn('rounded-xl border p-3', c.warn && 'border-destructive bg-destructive/5')}>
                <div className="text-2xl font-bold">{c.v}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.l}</div>
              </div>
            ))}
          </div>
          {bounceRate > 3 && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" /> Rimbalzi sopra il 3%: rischio per la reputazione condivisa. Controlla chi sta rimbalzando.
            </div>
          )}

          {/* Azioni massive */}
          <div className="flex items-center gap-2">
            <Button
              onClick={() => bulkMut.mutate([...selected])}
              disabled={selected.size === 0 || bulkMut.isPending}
            >
              {bulkMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
              Attiva rete ({selected.size})
            </Button>
            <Button variant="outline" onClick={() => recheckMut.mutate()} disabled={recheckMut.isPending}>
              {recheckMut.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              Ricontrolla DNS in blocco
            </Button>
          </div>

          {/* Record generati dall'ultima attivazione */}
          {results.length > 0 && (
            <Card className="border-indigo-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Record DNS da inserire</CardTitle>
                <CardDescription>Incollali nel pannello DNS di ciascun dominio (Netsons, OVH, Aruba…).</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {results.map((r) => (
                  <div key={r.account} className="rounded-md border p-3">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      {r.ok ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-destructive" />}
                      {r.domain || r.account}
                      <span className="text-xs font-normal text-muted-foreground">{r.message}</span>
                    </div>
                    {r.records?.map((rec, i) => (
                      <div key={i} className="mt-2 text-xs font-mono bg-muted/40 rounded p-2 flex items-start gap-2">
                        <span className="flex-1 break-all">
                          <b>{rec.type}</b> · {rec.host} → {rec.value}
                        </span>
                        <button onClick={() => { void navigator.clipboard.writeText(rec.value); toast.success('Copiato'); }}>
                          <Copy className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Clienti */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Clienti ({clients.length})</CardTitle>
              <CardDescription>Seleziona le caselle da attivare sulla rete.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                {clients.map((c) => {
                  const bounce = c.sent_30d > 0 ? Math.round((c.bounced_30d / c.sent_30d) * 100) : 0;
                  return (
                    <div key={c.user_id} className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm">
                      <input
                        type="checkbox"
                        disabled={!c.account_id}
                        checked={c.account_id ? selected.has(c.account_id) : false}
                        onChange={() => c.account_id && toggle(c.account_id)}
                        className="h-4 w-4"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{c.email_address ?? c.email}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {c.full_name ?? '—'} · {c.sent_30d} inviate/30gg
                          {c.sent_30d > 0 && ` · ${bounce}% rimbalzi · ${c.opened_30d} aperte`}
                        </div>
                      </div>
                      {c.domain ? (
                        <>
                          <span className={cn(
                            'text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0',
                            c.enabled && c.brevo_authenticated ? 'bg-emerald-100 text-emerald-700'
                              : c.brevo_authenticated ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-amber-100 text-amber-700'
                          )}>
                            {c.enabled && c.brevo_authenticated ? 'In rete' : c.brevo_authenticated ? 'Pronto' : 'DNS da fare'}
                          </span>
                          {c.brevo_authenticated && (
                            <Button
                              size="sm"
                              variant={c.enabled ? 'outline' : 'default'}
                              className="h-7 shrink-0"
                              disabled={reteMut.isPending}
                              onClick={() => reteMut.mutate({ domain: c.domain!, enabled: !c.enabled })}
                            >
                              {c.enabled ? 'Togli dalla rete' : 'Metti in rete'}
                            </Button>
                          )}
                        </>
                      ) : (
                        <span className="text-[11px] text-muted-foreground shrink-0">non attivato</span>
                      )}
                      {bounce > 5 && <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <InviteCodes />
        </div>
      </div>
    </div>
  );
}
